import * as React from "react";

import {
  AccessReportsWizardAccessCriteria,
  buildStandard,
  EmployeeGroup,
  EmployeeGroupTypeEnum,
} from "@app/access-reports/shared";
import { AccessReportParametersModel } from "@app/access-reports/wizard";
import { JSX } from "react";

interface AccessReportSummaryProps {
  accessReportParameters: AccessReportParametersModel;
  employeeGroup?: EmployeeGroup;
}

class AccessReportSummary extends React.Component<AccessReportSummaryProps, Record<string, never>> {
  private accessReportsGroupedProperties: string[] = [
    "productType",
    "product",
    "standard",
    "calculationMethod",
    "distanceMethod",
    "memberSet",
    "logo",
    "companyName",
    "requestorName",
    "internalClientId",
    "reportTitle",
    "createdBy",
    "emailTo",
    "splitBySpecialty",
    "splitEmployeeGroupByStandard",
    "reportPages",
  ];

  constructor(props: AccessReportSummaryProps) {
    super(props);
  }

  public render() {
    return (
      <div id="access-reports-review" className="copy-text-default">
        {this.accessReportsGroupedProperties.map((ar: string, index: number) => (
          <React.Fragment key={index}>
            {this.buildReviewElement(ar)}
          </React.Fragment>
        ))}
      </div>
    );
  }

  private buildReviewElement = (element: string): React.ReactNode => {
    let title: string;
    let content: JSX.Element;

    switch (element) {
      case "productType":
        if (!!this.props.accessReportParameters.productType) {
          title = "Product Type";
          content = (
            <p className="mb-0">
              {this.props.accessReportParameters.productType.displayName ?
                this.props.accessReportParameters.productType.displayName :
                this.props.accessReportParameters.productType.name
              }
            </p>
          );
        }
        break;
      case "product":
        if (!!this.props.accessReportParameters.product) {
          title = "Product";
          content = (
            <React.Fragment>
              <p className="mb-0">
                {this.props.accessReportParameters.product.displayName ?
                  this.props.accessReportParameters.product.displayName :
                  this.props.accessReportParameters.product.name
                }
              </p>
              {!this.props.accessReportParameters.product.skipAccessAnalysis && this.props.accessReportParameters.product.accessCriterion.map((accessCriteria, index) => (
                accessCriteria.selected && (
                  <p className="mb-0" key={index}>
                    {accessCriteria.name} @ {buildStandard(accessCriteria, this.props.accessReportParameters)}
                  </p>
                )
              ))}
            </React.Fragment>
          );
        }
        break;
      case "standard":
        if (!this.props.accessReportParameters.product.skipAccessAnalysis && !!this.props.accessReportParameters.standard) {
          title = "Access Standard";
          content = (
            <p className="mb-0">
              {this.props.accessReportParameters.standard.displayName ?
                this.props.accessReportParameters.standard.displayName :
                this.props.accessReportParameters.standard.name
              }
            </p>
          );
        }
        break;
      case "calculationMethod":
        if (!this.props.accessReportParameters.product.skipAccessAnalysis && !!this.props.accessReportParameters.calculationMethod) {
          title = "Calculation Method";
          content = (
            <p className="mb-0">
              {this.props.accessReportParameters.calculationMethod.displayName ?
                this.props.accessReportParameters.calculationMethod.displayName :
                this.props.accessReportParameters.calculationMethod.name
              }
            </p>
          );
        }
        break;
      case "distanceMethod":
        if (!this.props.accessReportParameters.product.skipAccessAnalysis && !!this.props.accessReportParameters.distanceMethod) {
          title = "Distance Method";
          content = (
            <p className="mb-0">
              {this.props.accessReportParameters.distanceMethod.displayName ?
                this.props.accessReportParameters.distanceMethod.displayName :
                this.props.accessReportParameters.distanceMethod.name
              }
            </p>
          );
        }
        break;
      case "memberSet":
        if (!this.props.accessReportParameters.product?.skipAccessAnalysis && (!!this.props.employeeGroup || !!this.props.accessReportParameters.product)) {
          let fileName: string;
          let employeeGroupType: string;
          let employeeGroupStatus: string;

          if (!!this.props.employeeGroup) {
            fileName = this.props.employeeGroup.fileName;
            employeeGroupType = this.props.employeeGroup.employeeGroupType === EmployeeGroupTypeEnum.Census ? EmployeeGroupTypeEnum.Census : EmployeeGroupTypeEnum.Listing;
            employeeGroupStatus = this.props.employeeGroup.employeeGroupStatus;
          } else {
            const accessCriteria: AccessReportsWizardAccessCriteria = this.props.accessReportParameters.product.accessCriterion.find(x => x.memberSet);
            if (!!accessCriteria) {
              fileName = accessCriteria.memberSet.fileName;
              employeeGroupType = accessCriteria.memberSet.isCensus ? EmployeeGroupTypeEnum.Census : EmployeeGroupTypeEnum.Listing;
              employeeGroupStatus = accessCriteria.memberSet.employeeGroupStatus;
            }
          }

          title = "Member Set";
          content = (
            <React.Fragment>
              {!!fileName &&
                <p className="mb-0">
                  {fileName}
                </p>
              }

              <p className="mb-0">
                {employeeGroupType && employeeGroupType.charAt(0).toUpperCase() + employeeGroupType.slice(1)}
              </p>

              {employeeGroupStatus &&
                <p className="mb-0">
                  {employeeGroupStatus.charAt(0).toUpperCase() + employeeGroupStatus.slice(1)}
                </p>
              }
            </React.Fragment>
          );
        }
        break;
      case "logo":
        if (!!this.props.accessReportParameters.logo) {
          title = "Logo";
          content = (
            <p className="mb-0">
              {this.props.accessReportParameters.logo.displayName ?
                this.props.accessReportParameters.logo.displayName :
                this.props.accessReportParameters.logo.name
              }
            </p>
          );
        }
        break;
      case "companyName":
        if (!!this.props.accessReportParameters.companyName) {
          title = "Company Name";
          content = (
            <p className="mb-0">
              {this.props.accessReportParameters.companyName}
            </p>
          );
        }
        break;
      case "requestorName":
        if (!!this.props.accessReportParameters.requestorName) {
          title = "Requestor Name";
          content = (
            <p className="mb-0">
              {this.props.accessReportParameters.requestorName}
            </p>
          );
        }
        break;
      case "internalClientId":
        if (!!this.props.accessReportParameters.internalClientId) {
          title = "Internal Id";
          content = (
            <p className="mb-0">
              {this.props.accessReportParameters.internalClientId}
            </p>
          );
        }
        break;
      case "reportTitle":
        if (!!this.props.accessReportParameters.reportTitleOne || !!this.props.accessReportParameters.reportTitleTwo || !!this.props.accessReportParameters.reportTitleThree) {
          title = "Report Title";
          content = (
            <React.Fragment>
              {!!this.props.accessReportParameters.reportTitleOne && <p className="mb-0">{this.props.accessReportParameters.reportTitleOne}</p>}
              {!!this.props.accessReportParameters.reportTitleTwo && <p className="mb-0">{this.props.accessReportParameters.reportTitleTwo}</p>}
              {!!this.props.accessReportParameters.reportTitleThree && <p className="mb-0">{this.props.accessReportParameters.reportTitleThree}</p>}
            </React.Fragment>
          );
        }
        break;
      case "createdBy":
        if (!!this.props.accessReportParameters.createdByOne || !!this.props.accessReportParameters.createdByTwo || !!this.props.accessReportParameters.createdByThree) {
          title = "Created By";
          content = (
            <React.Fragment>
              {!!this.props.accessReportParameters.createdByOne && <p className="mb-0">{this.props.accessReportParameters.createdByOne}</p>}
              {!!this.props.accessReportParameters.createdByTwo && <p className="mb-0">{this.props.accessReportParameters.createdByTwo}</p>}
              {!!this.props.accessReportParameters.createdByThree && <p className="mb-0">{this.props.accessReportParameters.createdByThree}</p>}
            </React.Fragment>
          );
        }
        break;
      case "emailTo":
        if (!!this.props.accessReportParameters.accessSharedOutput?.to) {
          title = "Email To";
          content = (
            <p className="mb-0">
              {this.props.accessReportParameters.accessSharedOutput.to}
            </p>
          );
        }
        break;
      case "splitBySpecialty":
        if (this.props.accessReportParameters.splitPagesBySpecialty) {
          title = "Split By Specialty";
          content = (
            <p className="mb-0">
              true
            </p>
          );
        }
        break;
      case "splitEmployeeGroupByStandard":
        if (this.props.accessReportParameters.splitEmployeeGroupByStandard) {
          title = "Split Employee Group By Standard";
          content = (
            <p className="mb-0">
              true
            </p>
          );
        }
        break;
      case "reportPages":
        const summaryPages = this.props.accessReportParameters.accessReportsWizardSummaryPages.filter(x => x.accessSummary);
        const detailsPages = this.props.accessReportParameters.accessReportsWizardDetailsPages.filter(x => x.accessDetail);
        const employeeCountPages = this.props.accessReportParameters.employeeCountPages.filter(x => x.employeeCount);
        const accessOverviewPages = this.props.accessReportParameters.accessOverviewPages.filter(x => x.accessOverview);
        const accessListingPages = this.props.accessReportParameters.accessListingPages.filter(x => x.accessListing);
        const radiusSearchPages = this.props.accessReportParameters.accessRadiusSearchPages.filter(x => x.accessRadiusSearch);
        const providerCountPages = this.props.accessReportParameters.providerCountPages.filter(x => x.providerCount);

        if (this.props.accessReportParameters.coverPage ||
          this.props.accessReportParameters.contentsPage ||
          this.props.accessReportParameters.separatorPage ||
          summaryPages.length > 0 ||
          detailsPages.length > 0 ||
          employeeCountPages.length > 0 ||
          accessOverviewPages.length > 0 ||
          accessListingPages.length > 0 ||
          radiusSearchPages.length > 0 ||
          providerCountPages.length > 0
        ) {
          title = "Report Pages";
          content = (
            <React.Fragment>
              {this.props.accessReportParameters.coverPage &&
                <p className="mb-0">
                  Cover Page
                </p>
              }
              {this.props.accessReportParameters.contentsPage &&
                <p className="mb-0">
                  Contents Page
                </p>
              }
              {this.props.accessReportParameters.separatorPage &&
                <p className="mb-0">
                  Separator Page
                </p>
              }
              {summaryPages.map((summaryPage, index) => (
                <p className="mb-0" key={index}>
                  Access Summary Page
                  {summaryPage.accessFilter && (
                    " with access filter of " + (summaryPage.accessFilter.displayName ? summaryPage.accessFilter.displayName : summaryPage.accessFilter.name)
                  )}
                  {summaryPage.summaryType && (
                    ((summaryPage.summarizeBy && summaryPage.summaryType.id === 1) ? ", " : " and ") + (summaryPage.summaryType.displayName ? summaryPage.summaryType.displayName : summaryPage.summaryType.name) + " summary type"
                  )}
                  {(summaryPage.summarizeBy && summaryPage.summaryType.id === 1) && (
                    ", and summarized by " + (summaryPage.summarizeBy.displayName ? summaryPage.summarizeBy.displayName : summaryPage.summarizeBy.name)
                  )}
                </p>
              ))}
              {detailsPages.map((detailsPage, index) => (
                <p className="mb-0" key={index}>
                  Access Details Page
                  {detailsPage.accessFilter && (" with access filter of " + (detailsPage.accessFilter.displayName ? detailsPage.accessFilter.displayName : detailsPage.accessFilter.name))}
                  {detailsPage.summarizeBy && (" and summarized by " + (detailsPage.summarizeBy.displayName ? detailsPage.summarizeBy.displayName : detailsPage.summarizeBy.name))}
                </p>
              ))}
              {employeeCountPages.map((employeeCountPage, index) => (
                <p className="mb-0" key={index}>
                  Employee Count Page
                  {(employeeCountPage.hasServiceArea && employeeCountPage.filteredWithinServiceArea) && (
                    " filtered " + (employeeCountPage.filteredWithinServiceArea.displayName ? employeeCountPage.filteredWithinServiceArea.displayName : employeeCountPage.filteredWithinServiceArea.name) + " service area and"
                  )}
                  {employeeCountPage.summarizeBy && (
                    " summarized by " + (employeeCountPage.summarizeBy.displayName ? employeeCountPage.summarizeBy.displayName : employeeCountPage.summarizeBy.name)
                  )}
                </p>
              ))}
              {accessOverviewPages.map((overviewPage, index) => (
                <p className="mb-0" key={index}>
                  Access Overview Page
                  {overviewPage.accessFilter && (" with access filter of " + (overviewPage.accessFilter.displayName ? overviewPage.accessFilter.displayName : overviewPage.accessFilter.name))}
                </p>
              ))}
              {accessListingPages.map((accessListingPage, index) => (
                <p className="mb-0" key={index}>
                  Access Listing Page
                  {accessListingPage.accessFilter && (" with access filter of " + (accessListingPage.accessFilter.displayName ? accessListingPage.accessFilter.displayName : accessListingPage.accessFilter.name))},
                  {!!accessListingPage.minCount && (` min count: ${accessListingPage.minCount},`)}
                  {!!accessListingPage.maxCount && (` max count: ${accessListingPage.maxCount},`)}
                  {!!accessListingPage.maxMiles && (` max miles: ${accessListingPage.maxMiles},`)}
                  {!!accessListingPage.maxMinutes && (` and max minutes: ${accessListingPage.maxMinutes},`)}
                </p>
              ))}
              {radiusSearchPages.map((radiusSearchPage, index) => (
                <p className="mb-0" key={index}>
                  Access Radius Search Page
                  {!!radiusSearchPage.accessFilter && ` with access filter of ${(radiusSearchPage.accessFilter.displayName ? radiusSearchPage.accessFilter.displayName : radiusSearchPage.accessFilter.name)},`}
                  {!!radiusSearchPage.searchByType && ` searching by ${(radiusSearchPage.searchByType.displayName ? radiusSearchPage.searchByType.displayName : radiusSearchPage.searchByType.name)},`}
                  {!radiusSearchPage.useExclusiveBands && " not"} using exclusive bands,
                  {!!radiusSearchPage.radiiOne && ` radii one: ${radiusSearchPage.radiiOne}`}
                  {!!radiusSearchPage.radiiTwo && `, radii two: ${radiusSearchPage.radiiTwo}`}
                  {!!radiusSearchPage.radiiThree && `, radii three: ${radiusSearchPage.radiiThree}`}
                  {!!radiusSearchPage.radiiFour && `, radii four: ${radiusSearchPage.radiiFour}`}
                  {!!radiusSearchPage.radiiFive && `, radii five: ${radiusSearchPage.radiiFive}`}
                </p>
              ))}
              {providerCountPages.map((providerCountPage, index) => (
                <p className="mb-0" key={index}>
                  Provider Count Page
                  {(providerCountPage.hasServiceArea && providerCountPage.filteredWithinServiceArea) && (
                    " filtered " + (providerCountPage.filteredWithinServiceArea.displayName ? providerCountPage.filteredWithinServiceArea.displayName : providerCountPage.filteredWithinServiceArea.name) + " service area and"
                  )}
                  {providerCountPage.summarizeBy && (
                    " summarized by " + (providerCountPage.summarizeBy.displayName ? providerCountPage.summarizeBy.displayName : providerCountPage.summarizeBy.name)
                  )}
                </p>
              ))}
            </React.Fragment>
          );
        }
        break;
      default:
        console.error(`Invalid Review Element Type: ${element}`);
        break;
    }


    if (!!title && !!content) {
      return (
        <div className="row mb-3">
          <div className="col-2">
            <b>{title}</b>
          </div>
          <div className="col-10">
            {content}
          </div>
        </div>
      );
    }
  }
}

export { AccessReportSummary };
